/**
 * createProviders(config): one place that picks every integration.
 *
 * MP_PROVIDERS is a comma-separated list of `family=impl` pairs; missing
 * families default to `dev`, so an empty value runs fully offline against a dev API:
 *
 *   ads   = dev | admob | h5 | none | auto      auto: admob (native), h5 (web), none (Discord)
 *   iap   = dev | stripe | native | none | auto auto: native (native), stripe (web), none (Discord)
 *   push  = dev | web | native | none | auto    auto: native (native), web (web), none (Discord)
 *   auth  = dev | none | auto | apple+google+discord
 *                                               auto: the configured IdPs for the platform
 *   review = dev | native | none | auto         auto: native (native, when the plugin is loaded), none (web, Discord).
 *                                               Unlike the others an unset review is `auto`, not `dev`: the release
 *                                               configs name the four families above, and a store build must not ask
 *                                               for reviews by logging them
 *
 * e.g. MP_PROVIDERS="ads=dev,iap=dev,push=web,auth=dev" (local), "ads=auto,iap=auto,push=auto,auth=auto" (release).
 * Crash reporting always uses console + API, plus Sentry when `crash.sentryDsn` is set.
 * Selecting a real provider without its config throws here, at startup. So does a
 * native (Capacitor) platform without `native` loaders: plugins load only through the
 * native entry chunk's literal import()s there (see native/index.ts), and native builds
 * sign in with Apple/Google through the system sheets (`auth.native`). An iOS build that
 * offers Google sign-in without Sign in with Apple throws too (App Store Guideline 4.8).
 */
import type { ApiClient } from './api.js';
import { AdMobProvider, type AdMobConfig } from './ads/admob.js';
import { DevAdsProvider, devAdsModeFromQuery, type DevAdsOptions } from './ads/dev.js';
import { H5GamesAdsProvider, type H5AdsConfig } from './ads/h5.js';
import { NoAdsProvider, type AdsProvider } from './ads/types.js';
import { AppleIdentityProvider, type AppleSignInConfig } from './auth/apple.js';
import { DevIdentityProvider, type DevIdentityOptions } from './auth/dev.js';
import { DiscordIdentityProvider, type DiscordSignInConfig } from './auth/discord.js';
import { EmailMagicLink } from './auth/email.js';
import { GoogleIdentityProvider, type GoogleSignInConfig } from './auth/google.js';
import { fetchAuthNonce, NativeIdentityProvider, NativeSocialLogin, type NativeSignInConfig } from './auth/native.js';
import type { IdentityName, IdentityProvider } from './auth/types.js';
import { ConsentStore } from './consent/store.js';
import { createCrashReporter } from './crash/index.js';
import type { CrashReporter } from './crash/types.js';
import { DevIapProvider } from './iap/dev.js';
import { GuardedIap, type PurchaseGuardState } from './iap/guard.js';
import { NativeIapProvider } from './iap/native.js';
import { StripeIapProvider, type StripeIapOptions } from './iap/stripe.js';
import { NoIapProvider, type IapProvider } from './iap/types.js';
import { requireNativeLoaders, type LinkConfig, type NativeLoaders } from './native/index.js';
import { detectPlatform, type Platform } from './platform/detect.js';
import { createHaptics, type Haptics } from './platform/haptics.js';
import { DevPushProvider } from './push/dev.js';
import { NativePushProvider, type NativePushChannel } from './push/native.js';
import { NoPushProvider, type PushProvider } from './push/types.js';
import { DevReviewProvider } from './review/dev.js';
import { NativeReviewProvider } from './review/native.js';
import { NoReviewProvider, type ReviewProvider } from './review/types.js';
import { WebPushProvider } from './push/web.js';
import { DeviceShareProvider, type ShareProvider } from './share/share.js';
import type { KV } from './storage.js';

const FAMILIES = {
  ads: ['dev', 'admob', 'h5', 'none', 'auto'],
  iap: ['dev', 'stripe', 'native', 'none', 'auto'],
  push: ['dev', 'web', 'native', 'none', 'auto'],
  review: ['dev', 'native', 'none', 'auto'],
  auth: ['dev', 'none', 'auto'],
} as const;
const IDPS: readonly IdentityName[] = ['apple', 'google', 'discord'];

type Family = keyof typeof FAMILIES;
export interface ProviderSelection {
  ads: (typeof FAMILIES.ads)[number];
  iap: (typeof FAMILIES.iap)[number];
  push: (typeof FAMILIES.push)[number];
  review: (typeof FAMILIES.review)[number];
  auth: (typeof FAMILIES.auth)[number] | IdentityName[];
}

export interface ResolvedSelection {
  ads: Exclude<ProviderSelection['ads'], 'auto'>;
  iap: Exclude<ProviderSelection['iap'], 'auto'>;
  push: Exclude<ProviderSelection['push'], 'auto'>;
  review: Exclude<ProviderSelection['review'], 'auto'>;
  /** 'dev' = dev tokens for every IdP; otherwise the real IdPs in use */
  auth: 'dev' | IdentityName[];
}

const isFamily = (k: string): k is Family => k in FAMILIES;

/** Parse an MP_PROVIDERS string; unknown families or values throw. */
export function parseProviders(spec: string): Partial<ProviderSelection> {
  const out: Partial<ProviderSelection> = {};
  for (const part of spec.split(',').map((s) => s.trim()).filter(Boolean)) {
    const [key = '', value = ''] = part.split('=').map((s) => s.trim());
    if (!isFamily(key)) throw new Error(`MP_PROVIDERS: unknown family "${key}" (expected ${Object.keys(FAMILIES).join(', ')})`);
    if (key === 'auth' && value.split('+').every((v) => (IDPS as readonly string[]).includes(v))) {
      out.auth = value.split('+') as IdentityName[];
      continue;
    }
    if (!(FAMILIES[key] as readonly string[]).includes(value)) throw new Error(`MP_PROVIDERS: ${key}=${value} is not one of ${FAMILIES[key].join(', ')}`);
    (out as Record<Family, string>)[key] = value;
  }
  return out;
}

export interface ProvidersConfig {
  /** MP_PROVIDERS string or an already-parsed selection */
  providers?: string | Partial<ProviderSelection>;
  api: ApiClient;
  kv: KV;
  platform?: Platform;
  /** location.search, for dev knobs such as ?ads=nofill */
  search?: string;
  /** the signed-in player's id (UUID): AdMob SSV user_id, StoreKit appAccountToken / Play obfuscatedAccountId */
  playerId?: () => string | null;
  /** the app's link scheme and hosts (apps/mobile/identity.json), for deep links from notifications */
  links?: LinkConfig;
  /** admob: the plugin comes from `native.admob`, the platform from `platform` */
  ads?: { dev?: DevAdsOptions; admob?: Omit<AdMobConfig, 'load' | 'platform'>; h5?: H5AdsConfig };
  iap?: {
    stripe?: Omit<StripeIapOptions, 'api'>;
    /** @deprecated use the top-level playerId */
    playerId?: () => string | null;
    /** Play subscriptions: product id -> base plan id */
    subscriptionPlans?: Record<string, string>;
  };
  auth?: {
    apple?: AppleSignInConfig;
    google?: GoogleSignInConfig;
    discord?: DiscordSignInConfig;
    dev?: Omit<DevIdentityOptions, 'kv'>;
    /** native builds: the system sign-in sheets (the web configs above are not used there) */
    native?: NativeSignInConfig;
  };
  push?: {
    vapidPublicKey?: string;
    swUrl?: string;
    /**
     * Extra Android notification channels to create from the WebView. The ones the server
     * sends on (PUSH_CHANNEL_IDS) are created natively at every start in the device's
     * language, so leaving this unset is the normal case; passing them again would
     * overwrite those names with this bundle's language.
     */
    channels?: readonly NativePushChannel[];
  };
  crash?: { release?: string; sentryDsn?: string; environment?: string };
  /** MO-06: wraps the IAP provider when given */
  purchaseGuard?: () => PurchaseGuardState;
  consentVersion?: number;
  hapticsEnabled?: () => boolean;
  /** required on native platforms: literal dynamic imports from the native entry chunk (NativeLoaders) */
  native?: NativeLoaders;
}

/** Native builds: the concrete providers behind the interfaces, for what only they do. */
export interface NativeProviders {
  loaders: NativeLoaders;
  /** requestTracking (ATT), gatherConsent / showPrivacyOptions (UMP), showRewarded(…, { personalized }) */
  ads: AdMobProvider | null;
  /** onTransaction (Ask to Buy approvals, renewals), resumeAll */
  iap: NativeIapProvider | null;
  /** listen (taps -> deep links, foreground notifications, token rotation) */
  push: NativePushProvider | null;
  /** signOut per IdP goes through identity[p] (NativeIdentityProvider) */
  identity: Partial<Record<'apple' | 'google', NativeIdentityProvider>>;
}

export interface Providers {
  selection: ResolvedSelection;
  platform: Platform;
  ads: AdsProvider;
  iap: IapProvider;
  identity: Partial<Record<IdentityName, IdentityProvider>>;
  email: EmailMagicLink;
  push: PushProvider;
  review: ReviewProvider;
  share: ShareProvider;
  crash: CrashReporter;
  consent: ConsentStore;
  haptics: Haptics;
  /** null on the web and Discord */
  native: NativeProviders | null;
}

const AUTO_IDPS: Record<Platform['kind'], IdentityName[]> = {
  web: ['apple', 'google'],
  'capacitor-ios': ['apple', 'google'],
  'capacitor-android': ['google'],
  discord: ['discord'],
};

export function resolveSelection(sel: Partial<ProviderSelection>, platform: Platform, configured: Partial<Record<IdentityName, unknown>> = {}): ResolvedSelection {
  const pick = <A extends string, B extends string, C extends string>(v: A | 'auto' | undefined, native: B, web: C): A | B | C | 'none' | 'dev' =>
    v === 'auto' ? (platform.native ? native : platform.kind === 'discord' ? 'none' : web) : (v ?? 'dev');
  const auth = sel.auth ?? 'dev';
  return {
    ads: pick(sel.ads, 'admob', 'h5') as ResolvedSelection['ads'],
    iap: pick(sel.iap, 'native', 'stripe') as ResolvedSelection['iap'],
    push: pick(sel.push, 'native', 'web') as ResolvedSelection['push'],
    review: pick(sel.review ?? 'auto', 'native', 'none') as ResolvedSelection['review'],
    auth: auth === 'dev' ? 'dev' : auth === 'none' ? [] : auth === 'auto' ? AUTO_IDPS[platform.kind].filter((p) => configured[p] !== undefined) : auth,
  };
}

function need<T>(value: T | undefined, what: string): T {
  if (value === undefined) throw new Error(`createProviders: ${what} is required by MP_PROVIDERS`);
  return value;
}

export function createProviders(c: ProvidersConfig): Providers {
  const platform = c.platform ?? detectPlatform();
  const native = requireNativeLoaders(platform, c.native);
  const parsed = typeof c.providers === 'string' ? parseProviders(c.providers) : (c.providers ?? {});
  const selection = resolveSelection(parsed, platform, native ? (c.auth?.native ?? {}) : (c.auth ?? {}));
  if (platform.kind === 'capacitor-ios' && selection.auth !== 'dev' && selection.auth.length > 0 && !selection.auth.includes('apple')) {
    throw new Error(`createProviders: iOS offers ${selection.auth.join(' + ')} sign-in without Sign in with Apple (App Store Guideline 4.8); configure auth.native.apple (VITE_APPLE_SIGN_IN=1)`);
  }
  const nativeOs = platform.kind === 'capacitor-ios' ? 'ios' : 'android';
  const playerId = c.playerId ?? c.iap?.playerId;
  const onNative = <K extends 'admob' | 'store' | 'push'>(k: K, what: string): NativeLoaders[K] => {
    if (!native) throw new Error(`createProviders: ${what} runs only on a Capacitor platform (with ProvidersConfig.native)`);
    return native[k];
  };

  const ads: AdsProvider = {
    dev: () => new DevAdsProvider({ mode: devAdsModeFromQuery(c.search ?? ''), ...c.ads?.dev }),
    admob: () => {
      const cfg = need(c.ads?.admob, 'ads.admob');
      return new AdMobProvider({ ...cfg, userId: need(cfg.userId ?? playerId, 'playerId (the AdMob SSV user_id)'), platform: nativeOs, load: onNative('admob', 'ads=admob') });
    },
    h5: () => new H5GamesAdsProvider(need(c.ads?.h5, 'ads.h5')),
    none: () => new NoAdsProvider(),
  }[selection.ads]();

  const baseIap: IapProvider = {
    dev: () => new DevIapProvider(c.api),
    stripe: () => new StripeIapProvider({ api: c.api, ...need(c.iap?.stripe, 'iap.stripe') }),
    native: () =>
      new NativeIapProvider({
        api: c.api,
        kv: c.kv,
        store: nativeOs === 'ios' ? 'apple' : 'google',
        load: onNative('store', 'iap=native'),
        ...(playerId ? { playerId } : {}),
        ...(c.iap?.subscriptionPlans ? { subscriptionPlans: c.iap.subscriptionPlans } : {}),
      }),
    none: () => new NoIapProvider(),
  }[selection.iap]();

  const push: PushProvider = {
    dev: () => new DevPushProvider(),
    web: () => new WebPushProvider({ vapidPublicKey: need(c.push?.vapidPublicKey, 'push.vapidPublicKey'), ...(c.push?.swUrl ? { swUrl: c.push.swUrl } : {}) }),
    native: () =>
      new NativePushProvider({
        platform: nativeOs,
        load: onNative('push', 'push=native'),
        api: c.api,
        kv: c.kv,
        ...(c.links ? { links: c.links } : {}),
        ...(c.push?.channels ? { channels: c.push.channels } : {}),
      }),
    none: () => new NoPushProvider(),
  }[selection.push]();

  // a native build without the plugin's loader asks for nothing, like the shell's optional plugins
  const review: ReviewProvider = {
    dev: () => new DevReviewProvider(),
    native: () => (native?.review ? new NativeReviewProvider(native.review) : new NoReviewProvider()),
    none: () => new NoReviewProvider(),
  }[selection.review]();

  const identity: Partial<Record<IdentityName, IdentityProvider>> = {};
  let social: NativeSocialLogin | null = null;
  const signInSheet = (p: 'apple' | 'google', loaders: NativeLoaders): NativeIdentityProvider => {
    const cfg = c.auth?.native;
    if (p === 'apple') {
      if (nativeOs !== 'ios') throw new Error('createProviders: Sign in with Apple on Android needs a server redirect endpoint, which the API does not have; use auth=google');
      need(cfg?.apple, 'auth.native.apple');
    } else if (nativeOs === 'ios') need(need(cfg?.google, 'auth.native.google').iosClientId, 'auth.native.google.iosClientId');
    else need(cfg?.google, 'auth.native.google');
    social ??= new NativeSocialLogin({ ...cfg, platform: nativeOs, load: loaders.socialLogin, nonce: () => fetchAuthNonce(c.api) });
    return new NativeIdentityProvider(p, social);
  };
  // the web popups bind each token to a fresh server nonce too, like the native sheets (IDP_REQUIRE_NONCE)
  const nonce = () => fetchAuthNonce(c.api);
  if (selection.auth === 'dev') {
    for (const p of IDPS) identity[p] = new DevIdentityProvider(p, { ...c.auth?.dev, kv: c.kv });
  } else {
    for (const p of selection.auth) {
      identity[p] =
        native && p !== 'discord'
          ? signInSheet(p, native)
          : p === 'apple'
            ? new AppleIdentityProvider({ nonce, ...need(c.auth?.apple, 'auth.apple') })
            : p === 'google'
              ? new GoogleIdentityProvider({ nonce, ...need(c.auth?.google, 'auth.google') })
              : new DiscordIdentityProvider(need(c.auth?.discord, 'auth.discord'));
    }
  }

  return {
    selection,
    platform,
    ads,
    iap: c.purchaseGuard ? new GuardedIap(baseIap, c.purchaseGuard) : baseIap,
    identity,
    email: new EmailMagicLink(c.api),
    push,
    review,
    share: new DeviceShareProvider(native ? { capacitorShare: native.share, filesystem: native.filesystem } : {}),
    crash: createCrashReporter({
      api: c.api,
      ...(c.crash?.release ? { release: c.crash.release } : {}),
      sentry: c.crash?.sentryDsn ? { dsn: c.crash.sentryDsn, ...(c.crash.environment ? { environment: c.crash.environment } : {}) } : null,
    }),
    consent: new ConsentStore(c.kv, c.consentVersion),
    haptics: createHaptics(platform, c.hapticsEnabled ?? (() => true), native?.haptics),
    native: native
      ? {
          loaders: native,
          ads: ads instanceof AdMobProvider ? ads : null,
          iap: baseIap instanceof NativeIapProvider ? baseIap : null,
          push: push instanceof NativePushProvider ? push : null,
          identity: { ...(identity.apple instanceof NativeIdentityProvider ? { apple: identity.apple } : {}), ...(identity.google instanceof NativeIdentityProvider ? { google: identity.google } : {}) },
        }
      : null,
  };
}
